import React, { useState } from 'react';
import { Navigate } from 'react-router';
import styled from 'styled-components';

import { getMainPrivateApi } from '#/api/apiCoreSdk';
import { useInvisibleReCaptcha } from '#/components/auth/Captcha/InvisibleCaptchaForm';
import { useForm } from '#/components/auth/form';
import AuthForm from '#/components/auth/layout/AuthForm';
import Button from '#/components/basic/Button/Button';
import LoadingText from '#/components/basic/Button/LoadingText';
import TextInput from '#/components/basic/inputs/TextInput';
import RouterLink from '#/components/basic/Link/RouterLink';
import LabelInputPanel from '#/components/basic/panels/LabelInputPanel';
import MetaTags from '#/components/layout/header/MetaTags';
import { useIsRestrictedRegion } from '#/hooks/useIsRestrictedRegion';
import { t } from '#/lib/display/formatting';
import { fetchAndDispatchUser } from '#/reduxState/currentUser/currentUserUtils';
import { screenSizes } from '#/reduxState/screen/screenUtils';
import { useDispatch, useSelector } from '#/reduxState/store';

const RegisterWrap = styled.div`
  padding-top: 14px;
  text-align: center;
`;

type Props = {
  disabledRedirect?: boolean;
};

function LoginPage({ disabledRedirect = false }: Props) {
  const dispatch = useDispatch();

  const isRestrictedRegion = useIsRestrictedRegion();
  const userLoggedIn = useSelector(state => state.currentUser.loggedIn);
  const isCatchAllUser = !!useSelector(
    state => state.currentUser.isCatchAllUser
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const screenSize = useSelector(state => state.screen.resolutionIndex);

  const { executeCaptchaAsync, renderCaptchaForm, resetRecaptcha } =
    useInvisibleReCaptcha();

  const { onSubmit, errorMessage, processing, redirect } = useForm(
    'Login',
    async () => {
      const captcha = await executeCaptchaAsync();
      if (!email || !password) {
        // type safety - this should never occur
        throw new Error('Login or Password not provided');
      }
      try {
        await getMainPrivateApi().authLogin(email, password, captcha);
      } catch (error) {
        resetRecaptcha();
        throw error;
      }
      await fetchAndDispatchUser(dispatch);
    }
  );

  const isMobile = screenSize <= screenSizes.smallest;

  if (redirect) {
    return <Navigate to={redirect} />;
  }

  if (
    disabledRedirect !== true &&
    userLoggedIn &&
    !isCatchAllUser &&
    !processing
  ) {
    return <Navigate to="/wallet/api-keys/manage" />;
  }

  const isDisabled = processing || isRestrictedRegion;

  return (
    <AuthForm
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      heading="Log in to Access API Keys"
    >
      <MetaTags title="Login" />

      <LabelInputPanel label="Email">
        <TextInput
          name="username"
          type="email"
          value={email || ''}
          onChange={setEmail}
          disabled={isDisabled}
          required
        />
      </LabelInputPanel>

      <LabelInputPanel stickLabelToTop label={t('Password')}>
        <TextInput
          name="password"
          type="password"
          value={password || ''}
          onChange={setPassword}
          disabled={isDisabled}
          required
        />
        <div style={{ padding: '15px 0px', float: 'right' }}>
          <RouterLink
            to={`/wallet/api-keys/password-reset#${email}`}
            disabled={isDisabled}
            color="white"
            underline
          >
            {t('Forgot your password?')}
          </RouterLink>
        </div>
      </LabelInputPanel>

      <LabelInputPanel noPadding>
        <div
          style={{
            display: 'flex',
            ...(isMobile
              ? { flexDirection: 'column' }
              : { alignItems: 'center', justifyContent: 'space-between' }),
          }}
        >
          <Button
            submit
            disabled={isDisabled}
            title={
              isRestrictedRegion
                ? 'This feature is not available in your region'
                : undefined
            }
          >
            <LoadingText processing={processing}>Log in</LoadingText>
          </Button>
        </div>
      </LabelInputPanel>

      <LabelInputPanel>
        <RegisterWrap>
          <RouterLink
            to="/wallet/api-keys/signup"
            disabled={isDisabled}
            underline
          >
            Register for API Keys
          </RouterLink>
        </RegisterWrap>
      </LabelInputPanel>

      <LabelInputPanel>{renderCaptchaForm}</LabelInputPanel>
    </AuthForm>
  );
}

export default LoginPage;
